import { useEffect, useState } from 'react';
import { api } from '@/lib/axios';

/**
 * Renders an image that lives behind a guarded API route (not a public
 * /uploads URL) — fetches it as a blob through the authenticated axios
 * instance and swaps in an object URL. Used for anything the storage
 * visibility split marked private (student/staff photos, etc.); plain
 * <img src> stays correct for genuinely public assets like logos.
 */
export function AuthImage({
  src,
  alt = '',
  className,
  fallback,
}: {
  src: string;
  alt?: string;
  className?: string;
  fallback?: React.ReactNode;
}) {
  const [objectUrl, setObjectUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let url: string | null = null;
    let cancelled = false;
    setFailed(false);
    setObjectUrl(null);

    api
      .get(src, { responseType: 'blob' })
      .then((res) => {
        if (cancelled) return;
        url = URL.createObjectURL(res.data);
        setObjectUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (url) URL.revokeObjectURL(url);
    };
  }, [src]);

  if (failed) return <>{fallback ?? null}</>;
  if (!objectUrl) return <>{fallback ?? null}</>;
  return <img src={objectUrl} alt={alt} className={className} />;
}
